import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { ApdmUploadButton } from '@euler/app/flows/apdm/imports/ApdmUploadButton';
import { VoiceRecordingBar } from '@euler/components/VoiceRecordingBar';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useVoiceDictation } from '@euler/functions/useVoiceDictation';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { ArrowUp, Check, Mic, Square, X } from 'lucide-react-native';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AttachmentChip } from '../components/AttachmentChip';
import { submitOnEnter } from '../components/submitOnEnter';
import { aiColors } from '../theme';

const kMaxAttachments = 5;
/** One line of text: the box's height with nothing wrapped. */
const kLineHeight = 20;
const kInputPadding = 8;
const kSingleLine = kLineHeight + kInputPadding * 2;
/** The box grows with the text up to about five lines, then scrolls. */
const kMaxLines = 5;

export interface AiChatComposerHandle {
  focus: () => void;
}

export const AiChatComposer = memo(
  forwardRef<
    AiChatComposerHandle,
    {
      busy: boolean;
      disabled: boolean;
      placeholder: string;
      onSend: (text: string, attachments: AiAttachmentInfo[]) => boolean;
      onStop: () => void;
    }
  >(({ busy, disabled, placeholder, onSend, onStop }, ref) => {
    const styles = useStyles();
    const insets = useSafeAreaInsets();
    const input = useRef<TextInput>(null);
    const mounted = useRef(true);
    const [text, setText] = useState('');
    const [attachments, setAttachments] = useState<AiAttachmentInfo[]>([]);
    const [height, setHeight] = useState(kSingleLine);
    const multiline = height > kSingleLine + 2;
    const changeText = (value: string) => {
      // The web input reports its height but never shrinks by itself:
      // start from one line again when text goes, and let it measure.
      if (value.length < text.length) setHeight(kSingleLine);
      setText(value);
    };
    useImperativeHandle(ref, () => ({ focus: () => input.current?.focus() }));
    useEffect(
      () => () => {
        mounted.current = false;
      },
      [],
    );
    const voice = useVoiceFeatures();
    const dictation = useVoiceDictation({
      onText: value => {
        if (mounted.current) setText(previous => previous + value);
      },
    });
    const listening = dictation.recording || dictation.recognizing;
    const ready =
      !disabled && !listening && (!!text.trim() || attachments.length > 0);
    const send = () => {
      if (ready && !busy && onSend(text, attachments)) {
        setText('');
        setHeight(kSingleLine);
        setAttachments([]);
      }
    };
    return (
      <View style={[styles.bar, { paddingBottom: insets.bottom + 8 }]}>
        {attachments.length > 0 && (
          <View style={styles.attachments}>
            {attachments.map(info => (
              <AttachmentChip
                key={info.id}
                info={info}
                onRemove={() =>
                  setAttachments(previous =>
                    previous.filter(item => item.id !== info.id),
                  )
                }
              />
            ))}
          </View>
        )}
        {/* One line: everything on its middle; more: the buttons stay at
            the bottom, by the last line. */}
        <View style={[styles.box, multiline && styles.boxMultiline]}>
          {dictation.recording ? (
            <>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="取消语音输入"
                onPress={dictation.cancel}
                style={styles.icon}
              >
                <X size={20} color={aiColors.mutedForeground} />
              </Pressable>
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <VoiceRecordingBar meter$={dictation.meter$} />
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="完成语音输入"
                onPress={dictation.complete}
                style={styles.icon}
              >
                <Check size={20} color={aiColors.primary} />
              </Pressable>
            </>
          ) : (
            <>
              <ApdmUploadButton
                appearance="icon"
                disabled={disabled || attachments.length >= kMaxAttachments}
                onUploaded={async info => {
                  if (!mounted.current) return;
                  setAttachments(previous =>
                    previous.some(item => item.id === info.id)
                      ? previous
                      : [...previous, info].slice(0, kMaxAttachments),
                  );
                }}
              />
              <TextInput
                ref={input}
                accessibilityLabel="输入消息"
                value={text}
                onChangeText={changeText}
                onKeyPress={submitOnEnter(send)}
                onContentSizeChange={event => {
                  const next = Math.min(
                    Math.max(
                      Math.ceil(event.nativeEvent.contentSize.height),
                      kSingleLine,
                    ),
                    kLineHeight * kMaxLines + kInputPadding * 2,
                  );
                  setHeight(previous => (previous === next ? previous : next));
                }}
                placeholder={placeholder}
                placeholderTextColor={aiColors.mutedForeground}
                multiline
                scrollEnabled={height >= kLineHeight * kMaxLines}
                editable={!disabled}
                style={[styles.input, { height }]}
              />
              {voice.isDictationEnabled && dictation.supported && (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="语音输入"
                  disabled={disabled || busy}
                  onPress={dictation.start}
                  style={styles.icon}
                >
                  {dictation.recognizing ? (
                    <ActivityIndicator size="small" color={aiColors.primary} />
                  ) : (
                    <Mic size={20} color={aiColors.mutedForeground} />
                  )}
                </Pressable>
              )}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={busy ? '停止回复' : '发送'}
                disabled={!busy && !ready}
                onPress={busy ? onStop : send}
                style={[
                  styles.send,
                  busy && styles.stop,
                  !busy && !ready && styles.sendDisabled,
                ]}
              >
                {busy ? (
                  <Square
                    size={14}
                    color={aiColors.primaryForeground}
                    fill="#fff"
                  />
                ) : (
                  <ArrowUp size={18} color={aiColors.primaryForeground} />
                )}
              </Pressable>
            </>
          )}
        </View>
      </View>
    );
  }),
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        bar: {
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: aiColors.border,
          backgroundColor: aiColors.card,
          paddingHorizontal: rpx(12),
          paddingTop: rpx(8),
        },
        attachments: { gap: rpx(8), marginBottom: rpx(8) },
        box: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          padding: rpx(6),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.input,
          width: '100%',
          maxWidth: rpx(760),
          alignSelf: 'center',
        },
        boxMultiline: { alignItems: 'flex-end' },
        input: {
          flex: 1,
          paddingHorizontal: rpx(4),
          paddingTop: kInputPadding,
          paddingBottom: kInputPadding,
          fontSize: rpx(14),
          lineHeight: kLineHeight,
          color: aiColors.foreground,
        },
        icon: {
          width: 36,
          height: 36,
          alignItems: 'center',
          justifyContent: 'center',
        },
        send: {
          width: 36,
          height: 36,
          borderRadius: 10,
          backgroundColor: aiColors.primary,
          alignItems: 'center',
          justifyContent: 'center',
        },
        sendDisabled: { opacity: 0.45 },
        // Stopping a reply, in the AI assistant's red.
        stop: { backgroundColor: aiColors.red },
      }),
    [rpx],
  );
}
